import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  type Theme,
} from '@mui/material';

import { PageHeader } from '@opengov/components-page-header';

import { MetricCard } from '../components/shared/MetricCard';
import { SectionCard } from '../components/vcm/SectionCard';
import { TierBar } from '../components/vcm/TierBar';
import { useVcm } from '../contexts/VcmContext';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import {
  mockGetCases,
  mockGetEventMetrics,
  mockGetOrgWorkloads,
  mockGetTierDistribution,
} from '../mocks/vcm-mock-data';

const eventMetrics = mockGetEventMetrics();
const orgWorkloads = mockGetOrgWorkloads();
const tierDistribution = mockGetTierDistribution();

/* MOCK DATA — case-status distribution, grouped from seeded cases. */
const caseStatusCounts: { status: string; count: number }[] = (() => {
  const counts = new Map<string, number>();
  for (const kase of mockGetCases()) {
    counts.set(kase.status, (counts.get(kase.status) ?? 0) + 1);
  }
  return [...counts.entries()].map(([status, count]) => ({ status, count }));
})();
const caseStatusTotal = caseStatusCounts.reduce((sum, row) => sum + row.count, 0);

/** Maps a case status to a Capital theme palette color for its proportion bar. */
function statusBarColor(status: string): (theme: Theme) => string {
  switch (status) {
    case 'Open':
      return (theme) => theme.palette.success.main;
    case 'Review':
      return (theme) => theme.palette.warning.main;
    case 'Pending':
      return (theme) => theme.palette.info.main;
    case 'Closed':
      return (theme) => theme.palette.text.secondary;
    default:
      return (theme) => theme.palette.primary.main;
  }
}

export function EventDashboardPage() {
  const { currentEvent } = useVcm();

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="Event Dashboard"
        description={`${currentEvent.name} \u00b7 ${currentEvent.programCode}`}
      />

      {/* ── Event KPIs ── */}
      {/* MOCK DATA — event-level metrics */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Event Clients"
            value={String(eventMetrics.eventClients)}
            subtitle={`${eventMetrics.clientsLast30Days} new in 30 days`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Open DCM Cases"
            value={String(eventMetrics.openDcmCases)}
            subtitle={`${eventMetrics.dcmCasePercentage}% of clients`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Open Referrals"
            value={String(eventMetrics.openReferrals)}
            subtitle={`${eventMetrics.referralsAwaitingOutcome} awaiting outcome`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Goals Completed"
            value={String(eventMetrics.goalsCompleted)}
            subtitle={`${eventMetrics.goalCompletionRate}% completion rate`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* ── Organization workload ── */}
        <Grid size={{ xs: 12, md: 7 }}>
          {/* MOCK DATA — per-organization workload */}
          <SectionCard title="Organization Workload" disableContentPadding>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Organization</TableCell>
                    <TableCell align="center">Open Cases</TableCell>
                    <TableCell align="center">Tier 3–4</TableCell>
                    <TableCell align="center">Follow-Ups Due</TableCell>
                    <TableCell align="center">Open Referrals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgWorkloads.map((row) => (
                    <TableRow key={row.organization} hover>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.organization}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{row.openCases}</TableCell>
                      <TableCell align="center">{row.tier34Cases}</TableCell>
                      <TableCell align="center">{row.followUpsDue}</TableCell>
                      <TableCell align="center">{row.openReferrals}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>

        {/* ── Tier distribution + Case status ── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            {/* MOCK DATA — tier distribution */}
            <SectionCard title="Tier Distribution">
              <Stack spacing={1.5}>
                {tierDistribution.map((row) => (
                  <TierBar
                    key={row.tier}
                    tier={row.tier}
                    count={row.count}
                    total={row.total}
                    description={row.description}
                    color={row.color}
                    accent={row.accent ?? false}
                  />
                ))}
              </Stack>
            </SectionCard>

            {/* MOCK DATA — case-status distribution */}
            <SectionCard title="Case status">
              <Stack spacing={1.5}>
                {caseStatusCounts.map((row) => {
                  const percentage =
                    caseStatusTotal > 0 ? Math.round((row.count / caseStatusTotal) * 100) : 0;
                  return (
                    <Box key={row.status}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        sx={{ mb: (theme) => theme.spacing(0.5) }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.status}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: (theme) => theme.palette.text.secondary }}
                        >
                          {row.count} · {percentage}%
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          height: (theme) => theme.spacing(1),
                          borderRadius: 1,
                          bgcolor: (theme) => theme.palette.action.hover,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${percentage}%`,
                            height: '100%',
                            borderRadius: 1,
                            bgcolor: statusBarColor(row.status),
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
